import { set } from "@vueuse/core";
import { onMounted, ref, toValue, watch } from "vue";
import { useStepperStore } from "../../stores/stepper.store.js";
import type { Block } from "../../stores/store.type.js";
import { useParentStep } from "./use.parent-step.js";

export function useStepperBlock(id: string, validatorFn: () => Promise<boolean>) {
	const { parentStepId, parentStep } = useParentStep();
	const isEnabled = ref(true);
	const isValid = ref(true);

	function registerBlock() {
		const parentId = toValue(parentStepId);
		if (!parentId) {
			return;
		}
		useStepperStore()
			.getState()
			.blocks.registerBlock({
				blockId: id,
				stepId: parentId,
				enabled: toValue(isEnabled),
				validate: validatorFn,
			});
	}

	function enableBlock() {
		useStepperStore().getState().blocks.enableBlock(id);
	}

	function disableBlock() {
		useStepperStore().getState().blocks.disableBlock(id);
	}

	onMounted(() => {
		registerBlock();
	});

	watch(parentStepId, () => {
		registerBlock();
	});

	useStepperStore().subscribe(async (state) => {
		const currentBlock = state.blocks.data.find((b) => b.blockId === id);

		if (currentBlock) {
			set(isEnabled, currentBlock.enabled);
			set(isValid, await currentBlock.validate(state));
		}
	});

	return { blockId: id, isEnabled, isValid, enableBlock, disableBlock, parentStep, parentStepId };
}

export function useOtherStepperBlock(id: string) {
	const block = ref<Block | null>(null);

	useStepperStore().subscribe((state) => {
		const currentBlock = state.blocks.data.find((b) => b.blockId === id);
		if (currentBlock) {
			set(block, currentBlock);
		}
	});

	function enableBlock() {
		useStepperStore().getState().blocks.enableBlock(id);
	}

	function disableBlock() {
		useStepperStore().getState().blocks.disableBlock(id);
	}

	return { block, enableBlock, disableBlock };
}
